import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Box,
  Paper,
  CircularProgress,
  LinearProgress,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Send as SendIcon,
  PersonPin as VcardIcon,
  CheckCircle as CheckCircleIcon,
  PhoneAndroid as PhoneIcon,
  Warning as WarningIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      maxWidth: 560,
      width: "100%",
      background:
        theme.palette.type === "dark"
          ? "linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 3, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& svg": { color: "#25d366" },
  },
  titleText: { fontWeight: 700, fontSize: "1.15rem" },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    maxHeight: "65vh",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  // ── Preview do contato a encaminhar ──
  sourceContactBox: {
    background:
      theme.palette.type === "dark"
        ? "rgba(37,211,102,0.08)"
        : "rgba(37,211,102,0.06)",
    border: `1px solid rgba(37,211,102,0.3)`,
    borderRadius: 10,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  sourceContactLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#25d366",
    marginBottom: 2,
  },
  sourceContactName: {
    fontWeight: 700,
    fontSize: "0.95rem",
  },
  sourceContactNumber: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  // ── Select de conexão ──
  connectionSelect: {
    "& .MuiOutlinedInput-root": { borderRadius: 10 },
  },
  connectedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.78rem",
    color: "#25d366",
    fontWeight: 500,
  },
  disconnectedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.78rem",
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  // ── Busca ──
  searchField: {
    "& .MuiOutlinedInput-root": { borderRadius: 10 },
  },
  // ── Lista de resultados ──
  resultsList: {
    maxHeight: 190,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "#25d366",
      borderRadius: 3,
    },
  },
  resultItem: {
    cursor: "pointer",
    transition: "background 0.15s",
    borderRadius: 8,
    margin: theme.spacing(0.25, 0.5),
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.04)",
    },
  },
  resultItemSelected: {
    background:
      theme.palette.type === "dark"
        ? "rgba(37,211,102,0.15)"
        : "rgba(37,211,102,0.08)",
  },
  selectedCheckIcon: { color: "#25d366" },
  // ── Chips ──
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    minHeight: 52,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.02)",
  },
  chip: {
    borderRadius: 8,
    fontWeight: 500,
    background:
      theme.palette.type === "dark"
        ? "rgba(37,211,102,0.2)"
        : "rgba(37,211,102,0.1)",
    color: theme.palette.type === "dark" ? "#86efac" : "#15803d",
    border: `1px solid rgba(37,211,102,0.35)`,
    "& .MuiChip-deleteIcon": {
      color: theme.palette.type === "dark" ? "#86efac" : "#16a34a",
      "&:hover": { color: theme.palette.error.main },
    },
  },
  emptyChips: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    fontStyle: "italic",
    margin: "auto",
  },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
  },
  // ── Progresso ──
  progressBox: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.03)",
  },
  progressLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  // ── Erros ──
  failedBox: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.error.light}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(239,68,68,0.08)"
        : "rgba(239,68,68,0.05)",
    padding: theme.spacing(1.5, 2),
  },
  failedTitle: {
    color: theme.palette.error.main,
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(0.5),
  },
  failedItem: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${theme.palette.error.light}`,
    marginBottom: 4,
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3, 2.5),
    gap: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  sendButton: {
    borderRadius: 10,
    fontWeight: 600,
    padding: theme.spacing(0.9, 2.5),
    textTransform: "none",
    fontSize: "0.95rem",
    background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #1db954 0%, #0a7a6f 100%)",
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  cancelButton: {
    borderRadius: 10,
    fontWeight: 500,
    textTransform: "none",
    fontSize: "0.95rem",
  },
  noResults: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
    fontSize: "0.85rem",
  },
}));

const getInitials = (name = "") =>
  name
    .trim()
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() || "")
    .join("");

const getAvatarColor = (name = "") => {
  const colors = ["#25d366", "#128c7e", "#075e54", "#34b7f1", "#6366f1", "#ec4899"];
  let hash = 0;
  for (let c of name) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return colors[Math.abs(hash) % colors.length];
};

const SendVcardModal = ({ open, onClose, sourceContact }) => {
  const classes = useStyles();

  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [loadingConnections, setLoadingConnections] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failedContacts, setFailedContacts] = useState([]);
  const [isDone, setIsDone] = useState(false);

  // Carregar conexões WhatsApp ao abrir
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setContacts([]);
      setSelectedContacts([]);
      setSelectedWhatsappId("");
      setProgress(0);
      setFailedContacts([]);
      setIsDone(false);
      setIsSending(false);
      return;
    }

    setLoadingConnections(true);
    api
      .get("/whatsapp/", { params: { showAll: true } })
      .then(({ data }) => {
        const connected = data.filter((w) => w.status === "CONNECTED");
        setWhatsapps(connected);
        if (connected.length === 1) setSelectedWhatsappId(connected[0].id);
      })
      .catch(toastError)
      .finally(() => setLoadingConnections(false));
  }, [open]);

  // Busca de contatos com debounce
  const searchContacts = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setContacts([]);
      return;
    }
    setIsSearching(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: term, pageNumber: 1 },
      });
      // Excluir o próprio contato de origem da lista
      const filtered = (data.contacts || []).filter(
        (c) => c.id !== sourceContact?.id
      );
      setContacts(filtered);
    } catch (err) {
      toastError(err);
    } finally {
      setIsSearching(false);
    }
  }, [sourceContact]);

  useEffect(() => {
    const timer = setTimeout(() => searchContacts(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm, searchContacts]);

  const isSelected = (contact) =>
    selectedContacts.some((c) => c.id === contact.id);

  const toggleContact = (contact) => {
    if (isSelected(contact)) {
      setSelectedContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } else {
      setSelectedContacts((prev) => [...prev, contact]);
    }
  };

  const removeContact = (contactId) => {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId));
  };

  const handleSend = async () => {
    if (!selectedWhatsappId) {
      toast.warn("Selecione uma conexão WhatsApp antes de enviar.");
      return;
    }
    if (selectedContacts.length === 0) {
      toast.warn("Selecione ao menos um contato destino.");
      return;
    }

    setIsSending(true);
    setProgress(0);
    setFailedContacts([]);
    setIsDone(false);

    try {
      const { data } = await api.post(
        `/contacts/${sourceContact.id}/vcard`,
        {
          destinationContactIds: selectedContacts.map((c) => c.id),
          whatsappId: selectedWhatsappId,
        }
      );

      const { sent, failed } = data;
      setProgress(100);
      setIsDone(true);

      if (failed && failed.length > 0) {
        setFailedContacts(failed);
        if (sent > 0) {
          toast.warning(
            `vCard enviado para ${sent} contato(s). ${failed.length} falha(s).`,
            { autoClose: 5000 }
          );
        } else {
          toast.error("Não foi possível enviar o vCard para nenhum contato.");
        }
      } else {
        toast.success(
          `vCard de "${sourceContact.name}" enviado com sucesso para ${sent} contato(s)!`,
          { autoClose: 4000 }
        );
        setTimeout(() => onClose(), 1800);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setIsSending(false);
    }
  };

  if (!sourceContact) return null;

  return (
    <Dialog
      open={open}
      onClose={!isSending ? onClose : undefined}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      {/* ── Título ── */}
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <VcardIcon style={{ fontSize: 28 }} />
        <Typography className={classes.titleText}>
          Encaminhar Contato (vCard)
        </Typography>
      </DialogTitle>

      {/* ── Conteúdo ── */}
      <DialogContent className={classes.dialogContent}>

        {/* Card do contato de origem */}
        <Box className={classes.sourceContactBox}>
          <Avatar
            src={sourceContact.profilePicUrl || ""}
            style={{
              width: 44,
              height: 44,
              background: getAvatarColor(sourceContact.name),
              fontWeight: 700,
            }}
          >
            {!sourceContact.profilePicUrl && getInitials(sourceContact.name)}
          </Avatar>
          <Box>
            <Typography className={classes.sourceContactLabel}>
              Contato a encaminhar
            </Typography>
            <Typography className={classes.sourceContactName}>
              {sourceContact.name}
            </Typography>
            <Typography className={classes.sourceContactNumber}>
              <PhoneIcon style={{ fontSize: 13 }} />
              {sourceContact.number || "Sem número"}
              {sourceContact.email && ` · ${sourceContact.email}`}
            </Typography>
          </Box>
        </Box>

        {/* Select de conexão */}
        <FormControl
          variant="outlined"
          size="small"
          fullWidth
          className={classes.connectionSelect}
          disabled={isSending || loadingConnections}
        >
          <InputLabel id="whatsapp-select-label">
            {loadingConnections ? "Carregando conexões..." : "Conexão WhatsApp *"}
          </InputLabel>
          <Select
            labelId="whatsapp-select-label"
            value={selectedWhatsappId}
            onChange={(e) => setSelectedWhatsappId(e.target.value)}
            label={loadingConnections ? "Carregando conexões..." : "Conexão WhatsApp *"}
          >
            {whatsapps.length === 0 && !loadingConnections && (
              <MenuItem disabled value="">
                <em>Nenhuma conexão ativa disponível</em>
              </MenuItem>
            )}
            {whatsapps.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <span style={{ fontWeight: 500 }}>{w.name}</span>
                  <span className={classes.connectedBadge}>
                    ● Conectado
                  </span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider />

        {/* Busca de contatos destino */}
        <Typography className={classes.sectionLabel}>
          👥 Contatos destino
        </Typography>
        <TextField
          className={classes.searchField}
          label="Buscar contato pelo nome..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isSending}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Resultados da busca */}
        {contacts.length > 0 && (
          <Paper variant="outlined" className={classes.resultsList}>
            <List dense disablePadding>
              {contacts.map((contact) => {
                const selected = isSelected(contact);
                return (
                  <ListItem
                    key={contact.id}
                    className={`${classes.resultItem} ${selected ? classes.resultItemSelected : ""}`}
                    onClick={() => !isSending && toggleContact(contact)}
                    button
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={contact.profilePicUrl || ""}
                        style={{
                          width: 36,
                          height: 36,
                          fontSize: "0.85rem",
                          background: getAvatarColor(contact.name),
                        }}
                      >
                        {!contact.profilePicUrl && getInitials(contact.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {contact.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          {contact.number || contact.email || ""}
                        </Typography>
                      }
                    />
                    {selected && (
                      <ListItemSecondaryAction>
                        <CheckCircleIcon
                          className={classes.selectedCheckIcon}
                          fontSize="small"
                        />
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}

        {!isSearching && searchTerm.length >= 2 && contacts.length === 0 && (
          <Typography className={classes.noResults}>
            Nenhum contato encontrado para "{searchTerm}"
          </Typography>
        )}

        {/* Chips dos selecionados */}
        <Box className={classes.chipsContainer}>
          {selectedContacts.length === 0 ? (
            <Typography className={classes.emptyChips}>
              Nenhum contato selecionado. Busque e selecione ao menos um.
            </Typography>
          ) : (
            selectedContacts.map((contact) => (
              <Chip
                key={contact.id}
                className={classes.chip}
                size="small"
                avatar={
                  <Avatar
                    src={contact.profilePicUrl || ""}
                    style={{
                      background: getAvatarColor(contact.name),
                      fontSize: "0.7rem",
                    }}
                  >
                    {!contact.profilePicUrl && getInitials(contact.name)}
                  </Avatar>
                }
                label={contact.name}
                onDelete={!isSending ? () => removeContact(contact.id) : undefined}
              />
            ))
          )}
        </Box>

        {/* Barra de progresso */}
        {isSending && (
          <Box className={classes.progressBox}>
            <Typography className={classes.progressLabel}>
              Enviando vCard para {selectedContacts.length} contato(s)...
            </Typography>
            <LinearProgress
              style={{ height: 8, borderRadius: 4 }}
              variant={progress === 0 ? "indeterminate" : "determinate"}
              value={progress}
              color="primary"
            />
          </Box>
        )}

        {/* Falhas */}
        {isDone && failedContacts.length > 0 && (
          <Box className={classes.failedBox}>
            <Typography className={classes.failedTitle}>
              ⚠️ Falhas no envio:
            </Typography>
            {failedContacts.map((f, i) => (
              <Typography key={i} className={classes.failedItem}>
                {f.name} — {f.reason}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* ── Ações ── */}
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.cancelButton}
          onClick={onClose}
          disabled={isSending}
          color="default"
        >
          Cancelar
        </Button>
        <Button
          className={classes.sendButton}
          onClick={handleSend}
          disabled={
            isSending ||
            !selectedWhatsappId ||
            selectedContacts.length === 0 ||
            isDone
          }
          startIcon={
            isSending ? (
              <CircularProgress size={16} style={{ color: "#fff" }} />
            ) : (
              <SendIcon />
            )
          }
          variant="contained"
          disableElevation
        >
          {isSending
            ? "Enviando..."
            : `Enviar vCard${selectedContacts.length > 0 ? ` (${selectedContacts.length})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendVcardModal;
